/**
 * Every image the game needs, in one place. The game loader, the
 * `assets:check` gate and the art brief for Codex are all generated from this
 * list, so it is the single source of truth for art requirements.
 *
 * Files live under frontend/public/assets/<path>/<state>_<n>.png and are
 * listed in frontend/public/assets/manifest.json (written by the art team).
 */

import { ICON_NAMES, BATCH_6_ICONS, BATCH_9_ICONS } from '../ui/icons';
import { SKIN_CATALOG } from './skinCatalog';
import { COSMETICS } from '../game/data/cosmetics';
import { SHOP_PRODUCTS } from '@shared/shop';
import { FORTUNE_TIERS } from '../game/data/fortune';
import { HERO_LOOKS } from '@shared/looks';

export type AssetGroup = 'characters' | 'battle' | 'fx' | 'ui' | 'items' | 'stickers' | 'icons' | 'props' | 'stamps';

export interface AssetSpec {
  /** Id used by code, e.g. "hero:pencil_knight". */
  id: string;
  group: AssetGroup;
  /** Delivery batch for the art pipeline. */
  batch: 1 | 2 | 3 | 4 | 5 | 6 | 8 | 9 | 10 | 11;
  /** Where the player sees it. */
  usedIn: string;
  /** Exact pixel size of every file of this asset. */
  size: [number, number];
  transparent: boolean;
  /** Normalised pivot inside the image (0..1). */
  anchor: [number, number];
  /** Animation states → number of frames. */
  states: Record<string, number>;
  facing?: 'right' | 'left';
  /** Folder under frontend/public/assets/. */
  path: string;
  /** Drawing notes for the artist. */
  notes: string;
  /** Rendered by PixiJS (packed into the GPU atlas) or by the DOM (<img>/CSS). */
  target: 'pixi' | 'dom';
  /** File format; `size` is the SVG viewBox size for svg. Default png. */
  format?: 'png' | 'svg' | 'webp';
  /**
   * Per-state canvas size / anchor when a pose needs more room than `size`
   * (e.g. a wide attack). The character must keep the same pixel scale.
   */
  stateSizes?: Record<string, [number, number]>;
  stateAnchors?: Record<string, [number, number]>;
}

export function stateSize(spec: AssetSpec, state: string): [number, number] {
  return spec.stateSizes?.[state] ?? spec.size;
}

export function stateAnchor(spec: AssetSpec, state: string): [number, number] {
  return spec.stateAnchors?.[state] ?? spec.anchor;
}

const CHAR_STATES = { idle: 3, attack: 1, hit: 1, ko: 1 };
/** Wide poses get a 768×512 canvas so the character keeps its idle scale. */
const WIDE = { attack: [768, 512], ko: [768, 512] } as Record<string, [number, number]>;
/** The body stays where it is in idle (x = 256 px); the extra width is in front of it. */
const WIDE_ANCHOR_RIGHT = { attack: [1 / 3, 0.96], ko: [1 / 3, 0.96] } as Record<string, [number, number]>;
const WIDE_ANCHOR_LEFT = { attack: [2 / 3, 0.96], ko: [2 / 3, 0.96] } as Record<string, [number, number]>;
const CHAR_NOTES =
  'Full body, standing on the baseline at 96% of the height, centred horizontally, ~430px tall. ' +
  'idle_0..2 = the same pose re-traced three times (tiny line differences = pencil "line boil"); ' +
  'attack_0 = leaning forward mid-strike; hit_0 = recoiling, eyes squeezed; ko_0 = knocked over / dizzy. ' +
  'No shadow (the game draws it). No text.';

type CharacterEntry = [id: string, name: string, description: string, batch?: 1 | 4 | 9 | 11];

const heroes: CharacterEntry[] = [
  ['pencil_knight', 'Pencil Knight', 'From the pack: brown-haired kid knight, red scarf, blue tunic, steel sword, notebook shield with a crown.'],
  ['crayon_mage', 'Crayon Mage', 'Kid mage, pointy blue hat with yellow stars, purple robe, orange crayon wand with a star tip.'],
  ['eraser_ninja', 'Eraser Ninja', 'Ninja whose body is a pink eraser with a blue sleeve, dark mask, red headband tails, grey paper shuriken.'],
  ['ruler_guard', 'Ruler Guard', 'Sturdy guard, green armour with ruler tick marks, wooden 30cm ruler as a spear, big blue shield.'],
  ['glue_alchemist', 'Glue Alchemist', 'Kid alchemist in a white lab coat with glue drips, safety goggles on the forehead, holding a bubbling test tube of white glue; glue bottle on the belt.', 4],
  ['highlighter_scout', 'Highlighter Scout', 'Nimble kid scout in a teal hoodie, neon-green highlighter used like a dagger, glowing neon streaks around it, map sticking out of a pocket.', 4],
  // Ready-made heroes sold in the shop (shared/looks.ts, batch 11): offered once their art is here.
  ...HERO_LOOKS.map((l): CharacterEntry => [l.id, l.name, `Ready-made hero sold in the shop, so make it the most polished character in the game: ${l.art}`, 11]),
];

const monsters: CharacterEntry[] = [
  ['gum_blob', 'Gum Blob', 'Green chewing-gum blob, one big eye, drips, wobbly mouth. Found under desk #4.'],
  ['homework_ghost', 'Homework Ghost', 'Ghost made of lined notebook paper (blue lines, red margin), big "F-" in red, hollow eyes.'],
  ['spitball_spider', 'Spitball Spider', 'Grey-blue paper spitball body, 8 thin legs, 5 eyes, little fangs.'],
  ['eraser_golem', 'Eraser Golem', 'From the pack (Eraser Beast): hulking green eraser creature with a pink eraser cap and pink claws, toothy grin.'],
  ['ink_octopus', 'Ink Octopus', 'Indigo fountain-pen-ink octopus, 5 curly tentacles, ink droplets around it.'],
  ['pencil_dragon', 'Pencil Dragon', 'Red dragon drawn on the last page, purple wing, yellow back spikes, breathing orange crayon fire.'],
  ['stapler_crab', 'Stapler Crab', 'Crab whose shell is a dark-red office stapler; the stapler jaw snaps like a claw, staples sticking out like spikes, small beady eyes.', 4],
  ['glue_monarch', 'Glue Monarch', 'Big wobbly white-glue blob wearing a paper crown, glue-bottle cap on its head, drips pooling on the floor, smug face.', 4],
  ['highlighter_wyvern', 'Highlighter Wyvern', 'Lean neon-green wyvern drawn with highlighter strokes, glowing yellow-green wings, cap-shaped head, drips of neon ink.', 4],
  ['principal_shadow', "Principal's Shadow", 'Final boss: tall dark-purple shadow in a suit and tie scribbled with black marker, glowing eyes behind glasses, clipboard in hand.', 4],
  // Season 1 (batch 9).
  ["crayon_wolf", "Crayon Wolf", "Wolf scribbled in many bright crayon colours, waxy strokes, broken-crayon teeth, wild fur.", 9],
  ["paint_splat", "Paint Splat", "Rainbow poster-paint puddle creature with paintbrush-stroke arms and paint-drop eyes.", 9],
  ["scissor_mantis", "Scissor Mantis", "Praying mantis whose forelegs are blue safety scissors, paper-green body, folded-paper wings.", 9],
  ["easel_titan", "Easel Titan", "Chapter boss, a bit bigger and grander: Giant wooden easel walking on three legs, angry scribbled face painted on its canvas, paint palette used as a shield.", 9],
  ["bookworm", "Bookworm Serpent", "Long green worm wearing round glasses, coiling out of an open hardcover book, page-shaped tongue.", 9],
  ["bookmark_bat", "Bookmark Bat", "Bat whose wings are long ribbon bookmarks with a tassel tail, tiny fangs.", 9],
  ["dusty_tome", "Dusty Tome", "Thick old encyclopedia golem with dust clouds, page-flap mouth, cracked leather-spine arms.", 9],
  ["shush_owl", "Shush Owl", "Chapter boss, a bit bigger and grander: Big brown owl in a knitted cardigan with reading glasses, wing raised to its beak saying shh, stacks of books.", 9],
  ["beaker_blob", "Beaker Blob", "Glass beaker with a fizzy green liquid blob climbing out of it, bubble eyes, foam drips.", 9],
  ["magnet_mole", "Magnet Mole", "Mole with a red horseshoe-magnet head, paperclips and pins stuck all over its body.", 9],
  ["volcano_toad", "Volcano Toad", "Toad shaped like a papier-mache volcano, red fizzy lava foam bubbling from its back.", 9],
  ["calc_bot", "Calculator Bot", "Chapter boss, a bit bigger and grander: Boxy robot built from a big school calculator, button chest, LCD face showing angry digits, antenna.", 9],
  ["dodgeball_ogre", "Dodgeball Ogre", "Chunky red ogre whose body is a rubber dodgeball, gym shorts, rubbery texture, big grin.", 9],
  ["jumprope_snake", "Jump-Rope Snake", "Snake made of a striped jump rope, wooden handles as its head and tail.", 9],
  ["whistle_hawk", "Whistle Hawk", "Hawk with a silver coach whistle as its beak on a red lanyard, sporty sweatband.", 9],
  ["sneaker_kaiju", "Sneaker Kaiju", "Chapter boss, a bit bigger and grander: Giant stomping sneaker monster, untied laces flailing like tentacles, sole with teeth, mud splashes.", 9],
  ["meatball_golem", "Meatball Golem", "Golem made of stacked meatballs with spaghetti arms and tomato-sauce drips.", 9],
  ["jelly_jiggler", "Jelly Jiggler", "Wobbly red jelly dessert monster on a paper plate, cherry on top, shiny highlights.", 9],
  ["milk_knight", "Milk Carton Knight", "Knight made from a school milk carton, drinking straw as a lance, carton flap helmet.", 9],
  ["mystery_hydra", "Mystery Meal Hydra", "Chapter boss, a bit bigger and grander: Three-headed hydra rising from a cafeteria lunch tray, heads made of noodles, peas and stew.", 9],
];

export const ASSET_CATALOG: AssetSpec[] = [
  ...heroes.map(([id, name, desc, batch = 1]): AssetSpec => ({
    id: `hero:${id}`,
    group: 'characters',
    batch,
    usedIn: `${name}: menu duel, notebook portrait, hero cards, battle (left side)`,
    size: [512, 512],
    transparent: true,
    anchor: [0.5, 0.96],
    states: CHAR_STATES,
    stateSizes: WIDE,
    stateAnchors: WIDE_ANCHOR_RIGHT,
    facing: 'right',
    path: `characters/heroes/${id}`,
    notes: `${desc} Faces RIGHT. ${CHAR_NOTES}`,
    target: 'pixi',
  })),
  ...monsters.map(([id, name, desc, batch = 1]): AssetSpec => ({
    id: `monster:${id}`,
    group: 'characters',
    batch,
    usedIn: `${name}: menu duel, monster collection cards, battle (right side)`,
    size: [512, 512],
    transparent: true,
    anchor: [0.5, 0.96],
    states: CHAR_STATES,
    stateSizes: WIDE,
    stateAnchors: WIDE_ANCHOR_LEFT,
    facing: 'left',
    path: `characters/monsters/${id}`,
    notes: `${desc} Faces LEFT. ${CHAR_NOTES}`,
    target: 'pixi',
  })),
  ...(['rock', 'paper', 'scissors'] as const).map(
    (m): AssetSpec => ({
      id: `move:${m}`,
      group: 'battle',
      batch: 2,
      usedIn: 'Move reveal above each fighter, How to Play rules',
      size: [128, 128],
      transparent: true,
      anchor: [0.5, 0.5],
      states: { idle: 1 },
      path: 'ui/buttons',
      notes: `From the pack: rps-${m}-normal.svg.`,
      target: 'pixi',
      format: 'svg',
    }),
  ),
  ...(['rock', 'paper', 'scissors'] as const).map(
    (m): AssetSpec => ({
      id: `button:${m}`,
      group: 'battle',
      batch: 2,
      usedIn: 'Battle RPS buttons (normal / hover / pressed / disabled)',
      size: [128, 128],
      transparent: true,
      anchor: [0.5, 0.5],
      states: { normal: 1, hover: 1, pressed: 1, disabled: 1 },
      path: 'ui/buttons',
      notes: `From the pack: rps-${m}-<state>.svg.`,
      target: 'dom',
      format: 'svg',
    }),
  ),
  fx('impact', 2, [320, 320], 'Impact burst when a hit lands', 'Radiating hand-drawn speed/impact lines around an empty centre, graphite only. 2 re-traced frames.'),
  fx('pow', 2, [320, 320], 'Critical hit burst', 'Yellow comic star-burst with "POW!" lettered in red crayon. 2 re-traced frames.'),
  fx('paper_scrap', 3, [96, 96], 'Hit particles, drifting menu scraps', 'Three different small torn scraps of lined paper (variants, not animation).'),
  fx('eraser_crumb', 3, [64, 64], 'Eraser dust on damage', 'Three different pink eraser crumb squiggles (variants).'),
  fx('shaving', 2, [96, 96], 'Hit particles', 'Two pencil-shaving curls, wood colour with a coloured rim (variants).'),
  fx('star', 1, [96, 96], 'Victory sparkle particles', 'Yellow crayon star with pencil outline.'),
  fx('heart', 1, [96, 96], 'Heal particles (Eraser Mend)', 'Pink crayon heart with pencil outline.'),
  { ...dom('ui:battle_bg', 'ui', [1920, 1080], false, [0.5, 1], 'backgrounds', 'Battle arena backdrop', 'From the pack: notebook-battle.webp.'), batch: 2, format: 'webp' },
  { ...dom('ui:hp_frame', 'ui', [300, 44], true, [0.5, 0.5], 'ui/panels', 'HP bars (battle)', 'From the pack: hp-bar-frame.svg.'), batch: 2, format: 'svg' },
  { ...dom('ui:exp_frame', 'ui', [300, 44], true, [0.5, 0.5], 'ui/panels', 'EXP bars (notebook, quests)', 'From the pack: exp-bar-frame.svg.'), batch: 2, format: 'svg' },
  { ...dom('ui:portrait_frame', 'ui', [180, 180], true, [0.5, 0.5], 'ui/panels', 'Hero portrait in the notebook', 'From the pack: portrait-frame.svg.'), batch: 2, format: 'svg' },
  dom('ui:notebook_cover', 'ui', [1600, 1000], false, [0, 0.5], 'ui/cover', 'Notebook-opening transition', 'Front cover of a blue school notebook, worn corners, white name label in the centre left blank, black elastic band on the right. Opaque.'),
  ...[
    ['pencil_sword', 'Pencil Sword'],
    ['golden_crayon', 'Golden Crayon'],
    ['compass_spear', 'Compass Spear'],
    ['cardboard_shield', 'Cardboard Shield'],
    ['eraser_armor', 'Eraser Armor'],
    ['ink_cape', 'Ink Cape'],
    ['lucky_sticker', 'Lucky Clover Sticker'],
    ['gold_star', 'Gold Star'],
    ['paper_crown', 'Paper Crown'],
  ].map(([id, name]) =>
    dom(`item:${id}`, 'items', [160, 160], true, [0.5, 0.5], `items/${id}`, 'Equipment slots, backpack, school shop, reward screen', `Item icon: ${name}. Single object, centred, fills ~80%.`),
  ),
  ...[
    ['stapler_gauntlet', 'Stapler Gauntlet: a glove with a red stapler on the back of the hand'],
    ['neon_highlighter', 'Neon Highlighter: glowing neon-green highlighter with sparkle marks'],
    ['glue_boots', 'Glue Boots: rain boots dripping with white glue'],
    ['marker_cape', 'Marker Cape: black cape covered in permanent-marker scribbles'],
    ['rubber_band', 'Rubber Band: a stretched red rubber band, mid-snap'],
    ['hall_pass', 'Hall Pass: wooden school hall-pass paddle with "PASS" written on it'],
  ].map(([id, desc]): AssetSpec => ({
    ...dom(`item:${id}`, 'items', [160, 160], true, [0.5, 0.5], `items/${id}`, 'Equipment slots, backpack, school shop, reward screen', `Item icon: ${desc}. Single object, centred, fills ~80%.`),
    batch: 4,
  })),
  // Season 1 items (batch 9).
  ...[
    ["sharpie_saber", "Sharpie Saber: a black permanent marker held like a lightsaber with an ink glow"],
    ["paintbrush_staff", "Paintbrush Staff: a tall paintbrush staff with rainbow paint on the bristles"],
    ["protractor_axe", "Protractor Axe: a battle axe whose blade is a clear plastic protractor"],
    ["calculator_club", "Calculator Club: a heavy club made from a big school calculator"],
    ["cardigan_coat", "Cardigan Coat: a cosy knitted brown cardigan with leather elbow patches"],
    ["lab_coat", "Lab Coat: a white science lab coat with a pen in the pocket and a small stain"],
    ["gym_jersey", "Gym Jersey: a red sports jersey with a big number 1"],
    ["lunch_tray_shield", "Lunch Tray Shield: a dented plastic cafeteria lunch tray used as a shield"],
    ["library_card", "Library Card: a library card with a smiling owl stamp"],
    ["golden_whistle", "Golden Whistle: a shiny golden coach whistle on a red lanyard"],
    ["science_medal", "Science Fair Medal: a gold science-fair medal with a beaker on it and a blue ribbon"],
    ["lunch_money", "Lunch Money: a small pile of shiny coins with a folded banknote"],
  ].map(([id, desc]): AssetSpec => ({
    ...dom(`item:${id}`, 'items', [160, 160], true, [0.5, 0.5], `items/${id}`, 'Equipment slots, backpack, school shop, reward screen', `Item icon: ${desc}. Single object, centred, fills ~80%.`),
    batch: 9,
  })),
  ...['star', 'heart', 'skull', 'crown', 'pencil'].map((id) =>
    dom(`sticker:${id}`, 'stickers', [128, 128], true, [0.5, 0.5], `stickers/${id}`, 'Memories diary, quest stamps', `Kid's sticker: ${id}, glossy sticker look with a white die-cut border.`),
  ),
  // Shared menu, notebook and creator icons; one requirement per icon name.
  ...ICON_NAMES.filter((name) => !BATCH_6_ICONS.includes(name) && !BATCH_9_ICONS.includes(name)).map(
    (name): AssetSpec => ({
      ...dom(`icon:${name}`, 'icons', [96, 96], true, [0.5, 0.5], `ui/icons/${name}`, 'Menu buttons, notebook tabs, stat rows, section titles', `Hand-drawn UI icon "${name}": thick graphite outline, crayon fill, readable at 24 px.`),
      batch: 5,
    }),
  ),
  // The skin kit owns scenery, paper, desk props, binding and titles.
  ...SKIN_CATALOG,
  ...BATCH_6_ICONS.map((name): AssetSpec => ({
    ...dom(`icon:${name}`, 'icons', [96, 96], true, [0.5, 0.5], `ui/icons/${name}`, 'Creator tools, random name, share actions, battle hint and rewards', `Hand-drawn ${name} icon with dark graphite outline and soft crayon colour. Readable at 24 px. Transparent.`), batch: 6,
  })),
  // Victory stamps (stationery shop + the contest champion prize); until delivered the game presses the word in CSS.
  ...COSMETICS.filter((c) => c.kind === 'stamp' && !c.premium && !c.gacha).map((c): AssetSpec => ({
    ...dom(
      c.id,
      'stamps',
      [256, 256],
      true,
      [0.5, 0.5],
      `ui/stamps/${c.id.slice('stamp:'.length)}`,
      c.prize ? 'Battle win card and the stationery shop, once the player won a weekly monster contest' : 'Battle win card and the stationery shop',
      `Rubber-stamp impression in ${c.color} ink reading "${c.text}", slightly uneven ink, worn edges, tilted a few degrees. Text must be legible. Transparent.`,
    ),
    batch: 8,
  })),
  // Special-shelf banners for the themed sets and the RGB pens (batch 10); until delivered the shelf shows the stamp shapes and inks.
  ...SHOP_PRODUCTS.filter((p) => p.kind === 'set' || p.sku === 'pack:rainbow').map((p): AssetSpec => ({
    ...dom(
      `shop:${p.sku.replace(':', '_')}`,
      'ui',
      [320, 200],
      true,
      [0.5, 0.5],
      `ui/shop/${p.sku.replace(':', '_')}`,
      'Special Shelf card of the stationery shop (Notebook → Gear)',
      `A cute flat-lay of the "${p.name}" on a sheet of notebook paper: ${p.note} Show the items as hand-drawn crayon doodles and stickers, no text, no price, bright and inviting. Transparent around the paper.`,
    ),
    batch: 10,
  })),
  // Fortune teller victory stamps (batch 10); pressed in CSS until delivered.
  ...COSMETICS.filter((c) => c.kind === 'stamp' && c.gacha).map((c): AssetSpec => ({
    ...dom(
      c.id,
      'stamps',
      [256, 256],
      true,
      [0.5, 0.5],
      `ui/stamps/${c.id.slice('stamp:'.length)}`,
      'Battle win card and the stationery shop, once won in the paper fortune teller',
      `Rubber-stamp impression in ${c.color} ink reading "${c.text}", slightly uneven ink, worn edges, tilted a few degrees, a little extra sparkle (it is a ${c.gacha} prize). Text must be legible. Transparent.`,
    ),
    batch: 10,
  })),
  // Paper fortune teller (Notebook → Gear); until delivered the game draws it in SVG/CSS (ui/fortuneTeller.ts).
  {
    ...dom(
      'fortune:teller',
      'ui',
      [512, 512],
      true,
      [0.5, 0.5],
      'ui/fortune/teller',
      'Fortune teller, outside view: picking a colour and the open/close pumping',
      'Folded paper fortune teller seen from straight above. Four outer pockets: blue top-left, pink top-right, green bottom-left, yellow bottom-right; ' +
        'a blank paper label (no text) at the centre of each pocket for the colour word the game writes. ' +
        'closed_0 = the four pockets touching; open_a_0 = top pair pulled up, bottom pair down (dark gap across the middle); open_b_0 = left pair pulled left, right pair right (gap down the middle).',
    ),
    states: { closed: 1, open_a: 1, open_b: 1 },
    batch: 10,
  },
  {
    ...dom(
      'fortune:inside',
      'ui',
      [512, 512],
      true,
      [0.5, 0.5],
      'ui/fortune/inside',
      'Fortune teller, inside view: picking a number and a flap',
      'The inside of the teller seen from above: white paper square creased into four triangular inner flaps pointing at the centre, ' +
        'an empty round number spot (no digit) at the centre of each quarter (25%/75% of the width and height).',
    ),
    batch: 10,
  },
  {
    ...dom(
      'fortune:flap_lift',
      'ui',
      [256, 256],
      true,
      [0.5, 0.5],
      'ui/fortune/flap_lift',
      'Fortune teller: the chosen inner flap folding back to show the prize',
      'One triangular inner flap (top-left quarter of the inside view) folded back over its crease, showing pencil-written lines underneath. The game rotates it for the other quarters.',
    ),
    batch: 10,
  },
  ...FORTUNE_TIERS.map((f): AssetSpec => ({
    ...dom(
      `fx:fortune_${f.tier}`,
      'fx',
      [384, 384],
      true,
      [0.5, 0.5],
      `fx/fortune_${f.tier}`,
      'Glow behind the fortune teller prize card',
      {
        common: 'Soft pencil-shaded halo with a few small grey sparkle marks; quiet.',
        rare: 'Sky-blue crayon halo with blue and white sparkle stars.',
        epic: 'Gold and purple crayon starburst with big sparkle stars and confetti dots.',
        legendary: 'Rainbow crayon starburst with gold glitter, hearts and stars bursting out; the most exciting of all.',
      }[f.tier] + ' Empty centre (the prize card sits there). Transparent.',
    ),
    batch: 10,
  })),
  ...BATCH_9_ICONS.map((name): AssetSpec => ({
    ...dom(`icon:${name}`, 'icons', [96, 96], true, [0.5, 0.5], `ui/icons/${name}`, 'Fortune teller tickets (Gear page, home note)', `Hand-drawn ${name} icon with dark graphite outline and soft crayon colour. Readable at 24 px. Transparent.`),
    batch: 10,
  })),
];

function fx(name: string, frames: number, size: [number, number], usedIn: string, notes: string): AssetSpec {
  return { id: `fx:${name}`, group: 'fx', batch: 2, usedIn, size, transparent: true, anchor: [0.5, 0.5], states: { idle: frames }, path: `fx/${name}`, notes, target: 'pixi' };
}

function dom(id: string, group: AssetGroup, size: [number, number], transparent: boolean, anchor: [number, number], path: string, usedIn: string, notes: string): AssetSpec {
  return { id, group, batch: 3, usedIn, size, transparent, anchor, states: { idle: 1 }, path, notes, target: 'dom' };
}

/** Conventional file path for a frame (relative to /assets/). */
export function framePath(spec: AssetSpec, state: string, index: number): string {
  if (spec.id.startsWith('skin:')) return `${spec.path}.png`;
  return `${spec.path}/${state}_${index}.${spec.format ?? 'png'}`;
}

/** Manifest written by the art team and read by the game. */
export interface AssetManifest {
  version: 1;
  pack: string;
  /**
   * Smaller runtime copies (WebP) of PNG masters, keyed by the PNG path, with
   * the SHA-1 of the PNG they were exported from (art/tools/export_runtime.py).
   */
  runtime?: Record<string, { file: string; sha1: string }>;
  assets: Record<
    string,
    {
      /** "pack" = taken from the asset pack as-is, "pack-edited" = adapted, "new" = newly drawn. */
      source: 'pack' | 'pack-edited' | 'new';
      /** Original file(s) inside the pack, when applicable. */
      packFiles?: string[];
      size: [number, number];
      anchor: [number, number];
      /** Per-state anchors when they differ from `anchor` (wide poses). */
      anchors?: Record<string, [number, number]>;
      /** state → frame files relative to /assets/. */
      states: Record<string, string[]>;
    }
  >;
}
